import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  GooglePlaceDto,
  UpdateLocationDto,
} from '@up2stay/up2promo-api-client';
import { LocationsApi } from '@up2stay/up2promo-api-client';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';

import type { LocationDetailDict } from '@/core/components/locations/location-detail-types';
import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';

type UseLocationDetailMutationsOptions = {
  workspaceId: string | undefined;
  id: string | undefined;
  d: LocationDetailDict | undefined;
  markSaved: () => void;
  markIdle: () => void;
  setLinkEntriesDirty: Dispatch<SetStateAction<boolean>>;
  onDeleted?: () => void;
};

export function useLocationDetailMutations({
  workspaceId,
  id,
  d,
  markSaved,
  markIdle,
  setLinkEntriesDirty,
  onDeleted,
}: UseLocationDetailMutationsOptions) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !id) return;
      await LocationsApi.remove({
        path: { workspaceId, id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      onDeleted?.();
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!workspaceId || !id) return;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: { name },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      markSaved();
    },
    onError: () => {
      toast.error(d?.editName?.error);
      markIdle();
    },
  });

  const updateContactMutation = useMutation({
    // Only the changed fields; '' clears a value (null used to be dropped,
    // so a cleared field came back after reload, DEV-557).
    mutationFn: async (payload: {
      contactEmail?: string;
      contactPhone?: string;
    }) => {
      if (!workspaceId || !id) return;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      markSaved();
    },
    onError: () => {
      toast.error(d?.contact?.error);
      markIdle();
    },
  });

  const updateMenuAndInstagramMutation = useMutation({
    mutationFn: async (payload: UpdateLocationDto) => {
      if (!workspaceId || !id) return;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: payload,
      });
    },
    onSuccess: () => {
      setLinkEntriesDirty(false);
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      markSaved();
    },
    onError: () => {
      toast.error(d?.externalServices?.error);
      markIdle();
    },
  });

  /** Review listing link only (Bewertungsprofile) — not address ownership. */
  const updateFromPlaceMutation = useMutation({
    mutationFn: async (place: GooglePlaceDto) => {
      if (!workspaceId || !id) return;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: {
          coordinates:
            place.latitude != null && place.longitude != null
              ? { latitude: place.latitude, longitude: place.longitude }
              : undefined,
          reviewExternalIds: { google: place.id },
          addressDetails: {
            formatted: place.address ?? undefined,
            postalCode: place.postalCode ?? undefined,
            street: place.street ?? undefined,
            streetNumber: place.streetNumber ?? undefined,
            locality: place.locality ?? undefined,
            administrativeArea: place.administrativeArea ?? undefined,
            administrativeAreaLevel2:
              place.administrativeAreaLevel2 ?? undefined,
            country: place.country ?? undefined,
            countryCode: place.countryCode ?? undefined,
          },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      markSaved();
    },
    onError: (err: Error) => {
      const response = (
        err as {
          response?: {
            data?: {
              errors?: {
                reviewExternalIds?: string;
                google?: string;
              };
            };
          };
        }
      )?.response;
      const linkedError =
        response?.data?.errors?.reviewExternalIds === 'googlePlaceLinked' ||
        response?.data?.errors?.google === 'googlePlaceLinked';
      if (linkedError) {
        toast.error(
          d?.integrations?.googlePlaces?.unlinkSyncBeforeChange ??
            'Unlink review sync first to change the listing.',
        );
        markIdle();
        return;
      }
      toast.error(d?.integrations?.googlePlaces?.linkError);
      markIdle();
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: async (payload: {
      addressDetails: LocationAddressDetails | null;
      latitude: number | undefined;
      longitude: number | undefined;
    }) => {
      if (!workspaceId || !id) return;
      const details = payload.addressDetails;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: {
          coordinates:
            payload.latitude !== undefined && payload.longitude !== undefined
              ? { latitude: payload.latitude, longitude: payload.longitude }
              : null,
          addressDetails: details
            ? {
                formatted: details.formatted ?? undefined,
                postalCode: details.postalCode ?? undefined,
                street: details.street ?? undefined,
                streetNumber: details.streetNumber ?? undefined,
                locality: details.locality ?? undefined,
                administrativeArea: details.administrativeArea ?? undefined,
                administrativeAreaLevel2:
                  details.administrativeAreaLevel2 ?? undefined,
                country: details.country ?? undefined,
                countryCode: details.countryCode ?? undefined,
              }
            : null,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      markSaved();
    },
    onError: () => {
      toast.error(d?.address?.error);
      markIdle();
    },
  });

  const clearListingMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !id) return;
      await LocationsApi.update({
        path: { workspaceId: workspaceId, id },
        body: { reviewExternalIds: { google: null } },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, id),
      });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      toast.success(d?.integrations?.googlePlaces?.listingClearSuccess);
    },
    onError: (err: Error) => {
      const response = (
        err as {
          response?: {
            data?: {
              errors?: {
                reviewExternalIds?: string;
                google?: string;
              };
            };
          };
        }
      )?.response;
      const linkedError =
        response?.data?.errors?.reviewExternalIds === 'googlePlaceLinked' ||
        response?.data?.errors?.google === 'googlePlaceLinked';
      if (linkedError) {
        toast.error(d?.integrations?.googlePlaces?.unlinkSyncBeforeChange);
        return;
      }
      toast.error(d?.integrations?.googlePlaces?.listingClearError);
    },
  });

  return {
    deleteMutation,
    updateNameMutation,
    updateContactMutation,
    updateMenuAndInstagramMutation,
    updateFromPlaceMutation,
    updateAddressMutation,
    clearListingMutation,
  };
}
